import { useGetStrategiesQuery } from 'generated/graphql';
import { Async, toAsyncFetched } from '@client/new/helpers/types';

export type CaseStudy = {
  id: string;
  title: string;
  subtitle: string | null;
  coverImageUrl: string;
  creatorName: string;
  creatorInstagramName: string;
  documentUrl: string;
  strategyName: string | null;
};

export type InspirationStoriesModel = {
  recommendedCaseStudies: CaseStudy[];
  otherCaseStudies: CaseStudy[];
  strategyName: string | null;
};

const mapCaseStudy = (
  cs: {
    id: string;
    title: string;
    subtitle?: string | null;
    coverImageVerticalUrl: string;
    creatorName: string;
    creatorInstagramName: string;
    documentUrl: string;
  },
  strategyName: string | null,
): CaseStudy => ({
  id: cs.id,
  title: cs.title,
  subtitle: cs.subtitle ?? null,
  coverImageUrl: cs.coverImageVerticalUrl,
  creatorName: cs.creatorName,
  creatorInstagramName: cs.creatorInstagramName,
  documentUrl: cs.documentUrl,
  strategyName,
});

export const useInspirationStoriesModel = (
  creatorId: number,
): Async<InspirationStoriesModel> => {
  const { data, loading, error } = useGetStrategiesQuery({
    variables: { creatorId: String(creatorId) },
    skip: !creatorId,
  });

  if (loading) return { type: 'fetching' };
  if (error) return { type: 'fetch-error', value: error.message };
  if (!data) return { type: 'not-fetched' };

  const activeStrategy = data.GetStrategies.find((s) => s.isSelected);
  const otherStrategies = data.GetStrategies.filter((s) => !s.isSelected);

  const recommendedCaseStudies: CaseStudy[] =
    activeStrategy?.caseStudies.map((cs) => mapCaseStudy(cs, null)) ?? [];

  const otherCaseStudies: CaseStudy[] = otherStrategies.flatMap((s) =>
    s.caseStudies.map((cs) => mapCaseStudy(cs, s.name)),
  );

  return toAsyncFetched({
    recommendedCaseStudies,
    otherCaseStudies,
    strategyName: activeStrategy?.name ?? null,
  });
};
